// import external libraries
import { create } from 'zustand';

// import enums
import {
  RosterDisplayTypeEnum,
  UserPreferenceValueEnums
} from './RosterPlan.enums';

// import types
import type {
  EmployeeNameFormatTypes,
  RosterPlanPreferencesInput,
  RosterPlanState,
  RosterPlanStore
} from './RosterPlan.types';

/** The name format the grid falls back to when the user has no stored preference. */
const DEFAULT_EMPLOYEE_NAME_FORMAT: EmployeeNameFormatTypes =
  'nameCompoundSurname';

/**
 * What the screen opens on before any preference is read: nothing selected, no extra columns, and
 * no mode picked — `useRosterPlanMode` derives the opening mode from the grants.
 *
 * A factory rather than a shared constant, because the empty collections would otherwise *be* the
 * live state: one in-place `selectedRequests.push(...)` would then poison the defaults for the rest
 * of the session and every later reset with it.
 */
export const createRosterPlanDefaults = (): RosterPlanState => ({
  selectedPlanUnits: [],
  showSortOrderColumn: false,
  employeeNameFormat: DEFAULT_EMPLOYEE_NAME_FORMAT,
  showSourceCostCenter: false,
  showCommitmentCostCenter: false,
  mode: null,
  display: RosterDisplayTypeEnum.MonthView,
  selectedStamp: null,
  selectedRequests: [],
  selectedDutyLines: []
});

/**
 * Derives the four preference mirrors from the persisted user preferences, reproducing the
 * `useState` initialisers this store replaces exactly — including their tolerance for a
 * `preferences` object the boot saga has not filled yet.
 */
const toPreferenceMirrors = (preferences: RosterPlanPreferencesInput) => ({
  showSortOrderColumn:
    preferences.rosterSorting?.some((sort) => sort.realAttr === 'sortorder') ??
    false,
  employeeNameFormat:
    preferences.rosterEmployeeNameDisplay ?? DEFAULT_EMPLOYEE_NAME_FORMAT,
  showSourceCostCenter:
    preferences.rosterEmployeeGridSourceCostCenter ===
    UserPreferenceValueEnums.Show,
  showCommitmentCostCenter:
    preferences.rosterEmployeeGridDestinationCostCenter ===
    UserPreferenceValueEnums.Show
});

/**
 * The single home for the roster screen's state, replacing the state that used to live in
 * `RosterPlan.tsx` (and, once #2499 and #2503/#2504 land, the Dojo `RosterModel`).
 *
 * Being a module singleton, it outlives unmounting the screen, so every mount has to put it back
 * into the state a first visit would have. That is split in two, deliberately:
 * `reset` puts *every* field back to its default on unmount, and `applyStoredPreferences` then
 * fills the four preference mirrors from Redux during the next mount's first render. Only the
 * second half is scoped to the mirrors; `reset` is deliberately total, so no field can survive a
 * visit by being forgotten here.
 *
 * The clearing cannot move to mount — `PlanUnitSelector` restores its own persisted selection and
 * emits it from a child effect, which React flushes first, so a mount-time clear would erase a
 * selection that is already in. See the clearing effect in `RosterPlan.tsx`.
 */
export const useRosterPlanStore = create<RosterPlanStore>()((set) => ({
  ...createRosterPlanDefaults(),
  setSelectedPlanUnits: (selectedPlanUnits) => set({ selectedPlanUnits }),
  setShowSortOrderColumn: (showSortOrderColumn) => set({ showSortOrderColumn }),
  setEmployeeNameFormat: (employeeNameFormat) => set({ employeeNameFormat }),
  setShowSourceCostCenter: (showSourceCostCenter) =>
    set({ showSourceCostCenter }),
  setShowCommitmentCostCenter: (showCommitmentCostCenter) =>
    set({ showCommitmentCostCenter }),
  setMode: (mode) => set({ mode }),
  setDisplay: (display) => set({ display }),
  setSelectedStamp: (selectedStamp) => set({ selectedStamp }),
  setSelectedRequests: (selectedRequests) => set({ selectedRequests }),
  setSelectedDutyLines: (selectedDutyLines) => set({ selectedDutyLines }),
  applyStoredPreferences: (preferences) =>
    set(toPreferenceMirrors(preferences)),
  reset: () => set(createRosterPlanDefaults())
}));

/**
 * Per-field selectors, so a consumer that needs one value does not subscribe to the whole store.
 * That matters here: the employees grid renders a row per employee — 10,000+ in a large tenant —
 * and a cell reading the store object would re-render the grid on every unrelated write.
 */
export const useSelectedPlanUnits = () =>
  useRosterPlanStore((state) => state.selectedPlanUnits);

export const useShowSortOrderColumn = () =>
  useRosterPlanStore((state) => state.showSortOrderColumn);

export const useEmployeeNameFormat = () =>
  useRosterPlanStore((state) => state.employeeNameFormat);

export const useShowSourceCostCenter = () =>
  useRosterPlanStore((state) => state.showSourceCostCenter);

export const useShowCommitmentCostCenter = () =>
  useRosterPlanStore((state) => state.showCommitmentCostCenter);

/** The mode as picked, unresolved; the screen reads `hooks/useRosterPlanMode` instead. */
export const usePickedRosterPlanMode = () =>
  useRosterPlanStore((state) => state.mode);

export const useRosterPlanDisplay = () =>
  useRosterPlanStore((state) => state.display);
